import type { Dispatch, SetStateAction } from 'react';
import React, { useEffect, useMemo, useRef } from 'react';
import queryString from 'query-string';
import { DesignLayoutType, PageKind } from '@graphql/generated-base';
import { Box, ColumnsV2, Flex, NoSsr } from '@withjoy/joykit';
import { useHistory } from '@react-router';
import { Footer } from '../../components/Footer';
import { Body } from './components/Body';
import { brannanConfig, useBrannanDesign } from './LayoutBrannan.theme';
import { findEventPageByPageSlug } from '../layout.utils';
import { LazyWelcomeVideo } from '../../widgets/WelcomeVideo/WelcomeVideo.lazy';
import CoverPhoto from '../../components/CoverPhoto';
import { brannanFooterContainerStyles } from '@apps/guest/packages/layout-engine/components/Footer/Footer.styles';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import { useLayoutBrannanController } from './LayoutBrannan.controller';
import { getEventMenuPropsFromEvent } from '@apps/guest/packages/layout-engine/layouts/layout.utils';
import { Shell } from './components/Shell';
import { MobileTopBarNav, MobileTopBarNavOffsetProvider } from '@apps/guest/packages/layout-engine/components/MobileTopBarNav';
import { isInIframe } from '@shared/utils/isInIframe';
import { sendMessageToParentWindow } from '@shared/utils/previewMessageBus';
import { useCurrentPageSlug } from '@shared/utils/hooks/useCurrentPageSlug';
import WebsiteHeader from './components/WebsiteHeader/WebsiteHeader';
import { useWebsiteHeaderSettings } from '../hooks/useWebsiteHeaderSettings/useWebsiteHeaderSettings';
import { useMasthead } from '../hooks/useMasthead/useMasthead';
import { isMastheadCompatibleLayout } from '@shared/utils/mastheadCompat';
import { isStandaloneHeroLayout } from '@shared/utils/standaloneHeroLayout';
import { useIsCompactBrannanHeader } from './components/HeaderV2/hooks/useIsCompactBrannanHeader';
import { CoverPhotoWrapper } from './components/CoverPhotoWrapper';
import type { LayoutBaseProps } from '../layout.types';
import { StandaloneWebsiteHeader } from './components/StandaloneWebsiteHeader/StandaloneWebsiteHeader';
import { useAccommodationPageChecks } from '@apps/guest/hooks/useAccommodationPageChecks';
import { useWelcomePage } from '@shared/hooks/useWelcomePage';
import { useDefaultPage } from '@shared/hooks/useDefaultPage';
import { useScrollToTop } from '@shared/components/ScrollToTop/ScrollToTop';
import { useResponsive } from '@shared/utils/hooks/useResponsive';
import { useGuestSitePages } from '../../hooks/useGuestSitePages';
import { getMigratedPageSlug, toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import { useEventPageRoute } from '@apps/guest/routes/GuestSite/GuestSite.routes';
import FooterNav from '../../components/FooterNav/FooterNav';
import { BrannanDesignProvider } from './BrannanDesignContext';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { useWelcomePagePhotoUrl } from '../hooks/useWelcomePagePhotoUrl';

export interface LayoutBrannanProps extends Readonly<LayoutBaseProps> {
  setLoadingTheme?: Maybe<Dispatch<SetStateAction<boolean>>>;
  isSinglePage?: boolean;
  setIsLastPageVisible?: (value: boolean) => void;
  setIsPastFirstPage?: (value: boolean) => void;
}

const { componentTitleBanner } = brannanConfig.applicatorHtmlProps;

/**
 * Floor for the content area when a standalone hero already occupies the top of the viewport.
 *
 * The hero renders at `100svh`, or `90svh` on mobile under the bouncing-chevron flag — so at worst
 * it leaves 10svh between its base and the fold. Reserving exactly that keeps the footer at or
 * below the fold no matter how sparse the page is, which is what the full-viewport floor was there
 * to guarantee (PRNT-4799).
 *
 * Non-binding in practice: real welcome pages carry far more than 10svh of content, so the box
 * takes its natural height and no gap appears. It only engages on near-empty pages, where without
 * it the footer creeps above the fold — measured at y=921 on a 941px viewport with 50px of content.
 */
const HERO_RESIDUAL_VIEWPORT_FLOOR = '10svh';

const LayoutBrannan: React.FC<LayoutBrannanProps> = ({ eventHandle, event, setLoadingTheme, hideNav }) => {
  const { info, pages, eventDesign } = event;

  const history = useHistory();

  const isPreviewing = isInIframe();
  const { currentPageSlug, isCustomPageSlug, isUrlBasedCustomPageSlug } = useCurrentPageSlug(eventHandle);

  const [isSm2orLarge] = useResponsive({ values: { _: false, sm2: true } });
  const [isGuestMobileFooterNavEnabled] = useIsFeatureEnabled('guestMobileFooterNavEnabled', { skip: isSm2orLarge });
  const [useRegistryLinksNav, isRegistryLinksNavLoading] = useIsFeatureEnabled('registryEnableGuestNavRegistryLinks');

  // Drops migrated legacy pages, plus the RSVP page when it is effectively disabled or past its deadline
  const { pages: filteredPages } = useGuestSitePages(pages, event.hasEnabledLegacyRsvpQuestions);

  const { handleMenuClicked, handleFooterButtonClicked, handleEditEventClicked, handleMenuOpenedChange } = useLayoutBrannanController(filteredPages);

  const { websiteHeader } = useWebsiteHeaderSettings({
    eventHandle,
    targetWebsiteLayout: 'brannan',
  });
  const welcomePhotoUrl = useWelcomePagePhotoUrl({ event, headerPhoto: websiteHeader?.headerPhoto });

  // Create eventProps with filtered pages for navigation menus (including hamburger menu)
  const eventMenuProps = useMemo(() => ({ ...getEventMenuPropsFromEvent(event), pages: filteredPages }), [event, filteredPages]);

  const { checkIsWelcomePage, loading: isWelcomePageCheckingLoading } = useWelcomePage();
  const { defaultPage, loading: isDefaultPageLoading } = useDefaultPage(filteredPages);

  // Determine if this is the root route (no slug in URL)
  const isRootRoute = !currentPageSlug;

  const { currentPage, isCustomPage } = useMemo(() => {
    // isCustomPageSlug is already aware of migrated pages when feature flag is ON (via useCurrentPageSlug)
    // For root route, use the default page (welcome page or first page)
    //
    // The fallback lookup (without isCustomPage) handles the transitional state where a migration
    // feature flag is ON but the page hasn't been migrated to type:custom yet. In that case,
    // isCustomPageSlug=true causes the first lookup to miss the still-legacy page, and the
    // fallback finds it by slug alone, preventing a spurious redirect to the default page.
    const currentPage = isRootRoute
      ? (defaultPage ?? null)
      : currentPageSlug
        ? (findEventPageByPageSlug(filteredPages, currentPageSlug, isCustomPageSlug, isUrlBasedCustomPageSlug) ??
          (isCustomPageSlug ? findEventPageByPageSlug(filteredPages, currentPageSlug) : null))
        : null;
    // CORE-6470: a custom page is any non-legacy kind (systemCustom | userCustom).
    const isCustomPage = !!currentPage && currentPage.pageKind !== PageKind.systemLegacy;
    return {
      currentPage,
      isCustomPage,
    };
  }, [currentPageSlug, isCustomPageSlug, isUrlBasedCustomPageSlug, filteredPages, isRootRoute, defaultPage]);

  const { isNativeAccommodationPage } = useAccommodationPageChecks(currentPage?.pageSlug);

  // Accent art hugs the top of the page card, so drop the card's desktop top padding —
  // only when the in-flow header (the accent's host) actually renders on this page.
  const masthead = useMasthead(eventDesign);
  const hasMasthead = !!masthead && !hideNav && !isNativeAccommodationPage && isMastheadCompatibleLayout(websiteHeader?.headerPhoto.presentationLayout);

  const isStandaloneHeroHeader = !!websiteHeader && !hideNav && isStandaloneHeroLayout(websiteHeader.headerPhoto.presentationLayout);

  const { isWelcomePage, isCustomWelcomePage } = useMemo(
    () => checkIsWelcomePage(currentPage?.pageSlug, currentPage?.type),
    [checkIsWelcomePage, currentPage?.pageSlug, currentPage?.type],
  );
  // Same predicate the background header layouts use to size themselves, so the floor below can
  // never disagree with the hero above it about whether the hero is full height.
  const { isCompactView: isCompactHeroHeader } = useIsCompactBrannanHeader({
    pages: filteredPages,
    pageSlug: currentPage?.pageSlug,
    currentPageType: currentPage?.type,
    brannanNavigation: eventDesign?.brannanNavigation,
  });

  // True when the standalone hero renders at its full `100svh`/`90svh` height above the frame,
  // pushing the frame's top past the fold on its own.
  //
  // The compact-view condition is load-bearing, not belt-and-braces: in compact view the in-flow
  // hero element collapses to height 0 and the full-bleed photo stays only as a `position: fixed`
  // backdrop, which reserves no space. Measured on /jai-jani/faq: the frame starts at y=0 and,
  // without the floor, the footer lands at y=724 on a 941px viewport — above the fold, exactly the
  // shift PRNT-4799 fixed.
  //
  // Sharing the hero's own predicate rather than testing `isWelcomePage` here matters twice over.
  // It covers events with no welcome page, where the root page sits at index 0 and the hero still
  // renders full height — those would otherwise keep the floor and the gap. And it keeps the two
  // in lockstep through the welcome-as-custom-page migration, where `isWelcomePage` resolves
  // asynchronously: gating on it alone let the floor flip 100vh -> 10svh after the flag landed,
  // shrinking the box and jerking the footer upward — a self-inflicted version of the very shift
  // this floor exists to prevent.
  const hasFullHeightHeroAboveFrame = isStandaloneHeroHeader && !isCompactHeroHeader;

  const eventPageRoute = useEventPageRoute(eventHandle, filteredPages);

  const queryParams = useQueryParams();

  const hasRedirectedRef = useRef(false);

  useEffect(() => {
    if (currentPageSlug === 'registrylinks' || currentPageSlug === 'registry') {
      // Wait for the remote rollout flag too. Without this, treatment users land the redirect
      // before Amplitude resolves and get bounced to /registry irrecoverably.
      if (isRegistryLinksNavLoading) return;
      // Redirect /registrylinks → /registry when registryEnableGuestNavRegistryLinks is OFF.
      // The Amplitude flag is the sole gate — applies to both migrated and non-migrated events.
      // Skip the redirect for user-created custom pages that happen to squat the 'registry'
      // slug — those are ordinary custom pages and should render normally.
      const isUserGeneratedSquatter = currentPage?.pageKind === PageKind.userCustom;
      if (!useRegistryLinksNav && !isUserGeneratedSquatter) {
        const path = queryString.stringifyUrl({
          url: `/${eventHandle}/registry`,
          query: queryParams,
        });
        window.location.href = path;
        return;
      }
    }

    // Wait for feature flags to load before determining redirect
    // This prevents race conditions where defaultPage is undefined because flags are still loading
    if (isWelcomePageCheckingLoading || isDefaultPageLoading) {
      return;
    }

    // Don't redirect if user has already redirected
    if (hasRedirectedRef.current) {
      return;
    }

    // Don't redirect for root route - it will render the default page
    // Only redirect for invalid slugs (slug provided but page not found)
    if (!currentPage && !isRootRoute && currentPageSlug) {
      // CORE-6488: Legacy slug redirect — when a URL contains a pre-migration slug (e.g.
      // /event/weddingparty from an already-sent email), filteredPages won't have it (it was
      // filtered out by useFilterMigratedLegacyPages). Search the unfiltered pages list to find
      // the page and redirect to its canonical URL so old links keep working.
      const legacyPage = pages.find(p => p.pageSlug === currentPageSlug);
      if (legacyPage) {
        // The pre-migration slug (e.g. 'weddingparty') no longer routes. Resolve the page's
        // canonical migrated slug from its type ('weddingparty' → vip → 'vip'). goToPath keys
        // off pageSlug alone, so it can't perform this legacy→canonical remap on its own.
        const canonicalSlug = getMigratedPageSlug(legacyPage.type) ?? toCanonicalMigratedSlug(legacyPage.pageSlug);
        if (canonicalSlug && canonicalSlug !== currentPageSlug) {
          // Prefer routing through the live (migrated) page so goToPath applies the allowlist /
          // `/page/` rules; fall back to the canonical short path if it isn't in filteredPages.
          const livePage = filteredPages.find(p => p.pageSlug === canonicalSlug || toCanonicalMigratedSlug(p.pageSlug) === canonicalSlug);
          const canonicalPath = livePage ? eventPageRoute.goToPath(livePage.type, livePage.pageSlug) : `/${eventHandle}/${canonicalSlug}`;
          // Only redirect if the canonical path differs from the current URL (avoids loops)
          if (canonicalPath && !canonicalPath.endsWith(`/${currentPageSlug}`)) {
            hasRedirectedRef.current = true;
            history.replace(
              queryString.stringifyUrl({
                url: canonicalPath,
                query: queryParams,
              }),
            );
            return;
          }
        }
      }

      // No legacy page found — fall back to default page.
      // Skip while previewing: in the editor preview an unknown slug means the admin just navigated
      // to a freshly-created page whose data hasn't arrived yet (the `adminEventPageChange` refetch
      // is in flight). Redirecting to the default page here would bounce the preview off the new
      // page and latch `hasRedirectedRef`, so it never lands. Once the refetch resolves, `filteredPages`
      // gains the page, this effect re-runs, and `currentPage` resolves — no redirect needed.
      if (defaultPage && !isPreviewing) {
        const path = eventPageRoute.goToPath(defaultPage.type, defaultPage.pageSlug);

        if (path) {
          hasRedirectedRef.current = true;

          history.replace(
            queryString.stringifyUrl({
              url: path,
              query: queryParams,
            }),
          );
        }
      }
    }
  }, [
    history,
    eventHandle,
    currentPage,
    currentPageSlug,
    queryParams,
    defaultPage,
    pages,
    filteredPages,
    eventPageRoute,
    isWelcomePageCheckingLoading,
    isDefaultPageLoading,
    isRootRoute,
    useRegistryLinksNav,
    isRegistryLinksNavLoading,
    isPreviewing,
  ]);

  const { isSmallScreen, reapplyDesign, loading } = useBrannanDesign(eventHandle, eventDesign);

  useEffect(() => {
    setLoadingTheme?.(loading);
  }, [loading, setLoadingTheme]);

  useEffect(() => {
    if (isPreviewing) {
      sendMessageToParentWindow({ action: 'requestIframeScaleValue', source: 'joyPreview' });
    }
  }, [isPreviewing, currentPage]);

  // Hide page photo for the welcome page when websiteHeader is present
  const shouldHidePagePhoto = isWelcomePage && websiteHeader;

  // To reduce the likelihood of preview falling out of sync in website designer,
  // we'll use the themeId as a key to force remount the Shell component
  const shellKey = isPreviewing ? event.eventDesign?.theme.themeId || 'default' : undefined;

  const shouldHideColumn = useMemo(() => {
    return isNativeAccommodationPage && !currentPage?.subTitle;
  }, [isNativeAccommodationPage, currentPage?.subTitle]);

  useScrollToTop({ behavior: 'instant' });

  return (
    <MobileTopBarNavOffsetProvider onMenuOpenChange={handleMenuOpenedChange}>
      <BrannanDesignProvider value={{ reapplyDesign }}>
        {/* Sticky mobile top-bar nav (experiment). Rendered for classic Brannan too, which serves
            non-native multi-page Brannan events. The component self-gates on isNative (returns null
            in the app), so it only appears on mobile web — no isNativeAccommodationPage gate needed. */}
        {!hideNav && (
          <MobileTopBarNav
            eventHandle={eventHandle}
            pages={filteredPages}
            layout={DesignLayoutType.brannan}
          />
        )}
        {/*  TODO: Question do we need to add !isNativeAccommodations check here - https://withjoy.atlassian.net/browse/PRNT-3157 */}
        {websiteHeader && !hideNav && (
          <StandaloneWebsiteHeader
            websiteHeader={websiteHeader}
            navBarVariant={isSmallScreen ? 'menu' : 'inline'}
            pages={filteredPages}
            currentPageType={currentPage?.type}
            eventHandle={eventHandle}
            handleMenuClicked={handleMenuClicked}
            eventProps={eventMenuProps}
            pageSlug={currentPage?.pageSlug}
            eventDesign={eventDesign}
          />
        )}
        <Shell
          eventDesign={event.eventDesign}
          isCustomPage={isCustomPage}
          topPadding={hasMasthead ? 'hidden' : 'auto'}
          styleApplicatorLoading={loading}
          key={shellKey}
        >
          {/* Header + page content fill at least the viewport, so the footer below them always
              starts at or past the fold. The page widget is <NoSsr>-wrapped and its data lands
              seconds after paint; without this floor the footer mounts into the empty content
              area (measured at y=684 on a 915px viewport) and the arriving content shoves it
              ~1795px down — a visible shift on every load, and the whole of this page's CLS.
              A floor rather than a loading placeholder: it holds before AND after the content
              swap, so the footer can't drift back up when a page's content is short. PRNT-4799.

              Relaxed to HERO_RESIDUAL_VIEWPORT_FLOOR where a full-height standalone hero already
              sits above this frame (see hasFullHeightHeroAboveFrame): there the hero has already
              pushed the frame most of the way down the viewport, so a second full viewport is dead
              space. It stacked ~500px of blank gap above the footer on welcome pages carrying only
              ~340px of real content — the "wide gap" couples reported, worst on password-gated
              sites where the sparse home page is the only page a guest sees. Measured on /jai-jani
              at 390x844: CLS 0.0038 with the full floor vs 0.0025 with the residual one, document
              225px shorter. Every other page keeps the full viewport floor. */}
          <Box
            minHeight={hasFullHeightHeroAboveFrame ? HERO_RESIDUAL_VIEWPORT_FLOOR : '100vh'}
            data-testid="layout-content"
          >
            {/* WebsiteHeader is above the fold — render during SSR */}
            {!isNativeAccommodationPage && !hideNav && (
              <WebsiteHeader
                data-testid={'layout-header'}
                websiteHeader={websiteHeader}
                applicatorProp={componentTitleBanner.prop}
                navBarVariant={isSmallScreen ? 'menu' : 'inline'}
                eventHandle={eventHandle}
                pages={filteredPages}
                info={info}
                handleMenuClicked={handleMenuClicked}
                handleEditEventClicked={handleEditEventClicked}
                eventProps={eventMenuProps}
                page={currentPage?.type}
                pageSlug={currentPage?.pageSlug}
                pageId={currentPage?.id}
                eventDisplayName={info?.eventDisplayName}
                masthead={masthead}
              />
            )}
            {/* Body chrome (photo, title, subtitle) SSRs; the PageWidget inside defers
            via NoSsr — see doc/improve-guest-site-perf/15-ssr-narrow-surface/PLAN.md */}
            <Body
              data-testid={`layout-body-${currentPageSlug}`}
              marginTop={[0, null, 9]}
              page={currentPage}
              event={event}
              eventHandle={eventHandle}
              isWelcomePage={isWelcomePage}
              isCustomWelcomePage={isCustomWelcomePage}
              photo={
                isWelcomePage && event.video ? (
                  <ColumnsV2.Column
                    style={isCustomPage ? { display: shouldHideColumn ? 'none' : 'block' } : {}}
                    span={[12, 10]}
                    offset={[0, 1]}
                  >
                    <LazyWelcomeVideo
                      video={event.video}
                      thumbnailUrl={event.video.thumbnailUrl || welcomePhotoUrl}
                    />
                  </ColumnsV2.Column>
                ) : shouldHidePagePhoto ? null : (
                  <CoverPhotoWrapper
                    renderFullColumn={currentPage?.photoPresentation?.presentationLayout !== 'center'}
                    isCustomPage={isCustomPage}
                    isNativeAccommodations={isNativeAccommodationPage}
                    subTitle={currentPage?.subTitle}
                  >
                    <CoverPhoto
                      websiteLayoutType={DesignLayoutType.brannan}
                      photo={currentPage?.photoV2}
                      pageId={currentPage?.id}
                      pageType={currentPage?.type}
                      pageSlug={currentPage?.pageSlug}
                      photoPresentation={currentPage?.photoPresentation}
                    />
                  </CoverPhotoWrapper>
                )
              }
            />
          </Box>
          <NoSsr>
            <Flex __css={brannanFooterContainerStyles}>
              {/* FooterNav is only visible on mobile */}
              {isGuestMobileFooterNavEnabled && !hideNav && (
                <Flex
                  width="100%"
                  display={{ _: 'flex', sm2: 'none' }}
                >
                  <FooterNav
                    pages={filteredPages}
                    eventHandle={eventHandle}
                    eventDisplayName={info?.eventDisplayName}
                    handleMenuClicked={handleMenuClicked}
                  />
                </Flex>
              )}
              <Footer
                onButtonClicked={handleFooterButtonClicked}
                data-testid={'layout-footer'}
              />
            </Flex>
          </NoSsr>
        </Shell>
      </BrannanDesignProvider>
    </MobileTopBarNavOffsetProvider>
  );
};

LayoutBrannan.displayName = 'LayoutBrannan';

export { LayoutBrannan };
